import { ApiCEFRLevel, ApiDifficulty, ApiLanguage, ApiTag } from './api.model';
import type { TeacherContentQuestion } from './content-api.model';

/** Wire shapes for api/v1/curriculum/student/* and api/v1/student/allcourses. */

export enum ApiCurriculumMediaType {
  THUMBNAIL, BANNER,
}

export interface CurriculumCourseMedia {
  id: string;
  curriculumCourseId: string;
  path: string;
  /** ApiCurriculumMediaType */
  type: number;
}

export interface StudentCurriculumLessonPlan {
  id: string;
  curriculumLessonId: string;
  title: string;
  description: string;
  estimationTime?: number;
  tags?: ApiTag[];
}

/** GET curriculum/teacher/courses/:id/lesson-plan */
export interface TeacherCourseLessonPlanStatus {
  exists: boolean;
  lessonPlan: StudentCurriculumLessonPlan | null;
}

/** PUT curriculum/teacher/courses/:id/lesson-plan */
export interface UpsertCurriculumLessonPlanInput {
  title: string;
  description: string;
  estimationTime?: number;
}

export interface StudentCurriculumLesson {
  id: string;
  curriculumChapterId: string;
  title: string;
  description: string;
  thumbnail_path?: string;
  difficulty: ApiDifficulty;
  orderItem?: number;
  tags?: ApiTag[];
  curriculumLessonPlan?: StudentCurriculumLessonPlan;
}

export interface StudentCurriculumChapter {
  id: string;
  curriculumCourseId: string;
  title: string;
  subtitle: string;
  length: number;
  color: string;
  difficulty: ApiDifficulty;
  description: string;
  thumbnail_path?: string;
  tags?: ApiTag[];
  curriculumLessons?: StudentCurriculumLesson[];
}

export interface StudentCourseTeacher {
  id: string;
  name: string;
  bio?: string;
  avatarUrl?: string;
}

export interface StudentCurriculumCourse {
  id: string;
  title: string;
  description: string;
  color?: string;
  duration?: number;
  language?: ApiLanguage;
  level?: ApiCEFRLevel;
  deadline?: string;
  tags?: ApiTag[];
  curriculumMedia?: CurriculumCourseMedia[];
  lexiconCategoryIds?: string[];
  curriculumChapters?: StudentCurriculumChapter[];
  teacher?: StudentCourseTeacher;
}

export enum CurriculumEnrollmentStatus {
  ACTIVE, COMPLETED, DROPPED, NEED_APPROVE, INACTIVE,
}

export function curriculumEnrollmentStatusName(
  status: CurriculumEnrollmentStatus | string | number | null | undefined,
): string {
  if (status === null || status === undefined) {
    return '';
  }
  if (typeof status === 'number') {
    return String(CurriculumEnrollmentStatus[status] ?? status);
  }
  const trimmed = String(status).trim();
  const asNum = Number(trimmed);
  if (trimmed !== '' && !Number.isNaN(asNum) && CurriculumEnrollmentStatus[asNum] !== undefined) {
    return String(CurriculumEnrollmentStatus[asNum]);
  }
  return trimmed.toUpperCase();
}

export function isPendingCourseEnrollment(
  status: CurriculumEnrollmentStatus | string | number | null | undefined,
): boolean {
  return curriculumEnrollmentStatusName(status) === 'NEED_APPROVE';
}

export function isInactiveCourseEnrollment(
  status: CurriculumEnrollmentStatus | string | number | null | undefined,
): boolean {
  return curriculumEnrollmentStatusName(status) === 'INACTIVE';
}

export function isLearnableCourseEnrollment(
  status: CurriculumEnrollmentStatus | string | number | null | undefined,
): boolean {
  const name = curriculumEnrollmentStatusName(status);
  return name === 'ACTIVE' || name === 'COMPLETED';
}

export function isRegisteredCourseEnrollment(
  status: CurriculumEnrollmentStatus | string | number | null | undefined,
): boolean {
  return isLearnableCourseEnrollment(status) || isPendingCourseEnrollment(status);
}

/** Enrollments the student still owns (including hidden inactive ones). */
export function isManagedCourseEnrollment(
  status: CurriculumEnrollmentStatus | string | number | null | undefined,
): boolean {
  const name = curriculumEnrollmentStatusName(status);
  return name !== 'DROPPED' && name !== '';
}

export interface StudentCurriculumEnrollment {
  id: string;
  curriculumCourseId: string;
  curriculumLessonPlanId: string;
  enrollmentStatus: CurriculumEnrollmentStatus;
  progressPercentage: number;
  completedChapters: number;
  completedAt?: string;
  tags?: ApiTag[];
  curriculumCourse?: Pick<
    StudentCurriculumCourse,
    'id' | 'title' | 'description' | 'color' | 'duration' | 'language' | 'curriculumMedia'
  >;
}

/** PATCH curriculum/student/lessons/:lessonId/progress */
export interface CurriculumLessonProgress {
  id: string;
  curriculumEnrollmentId: string;
  curriculumLessonId: string;
  progressPercentage: number;
  accuracy?: number | null;
}

export interface StudentCourseCatalogItem {
  id: string;
  title: string;
  description: string;
  color?: string;
  duration?: number;
  language?: ApiLanguage;
  deadline?: string;
  curriculumMedia?: CurriculumCourseMedia[];
  curriculumChapters?: Array<{ id: string; difficulty: ApiDifficulty }>;
}

export enum ApiPublicationStatus {
  DRAFT, REVIEW, APPROVED, REJECTED, PUBLISHED, UNPUBLISHED,
}

/** Teacher payloads return the full aggregate (audit + publication fields). */
export interface TeacherCurriculumCourse extends StudentCurriculumCourse {
  publication?: ApiPublicationStatus;
  status?: number;
  ownershipId?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Questions linked to lessons in this course (from teacher course GET). */
  contentQuestions?: TeacherContentQuestion[];
}

/*
 * Write models for POST/PATCH curriculum/teacher/courses. Children are created
 * through the root and carry no ids; `ownershipId` / `lastModifiedBy` are
 * filled in server-side from the JWT. Enum fields travel as the numeric API
 * value — produce them with the converters in `shared/enums`
 * (`languageToApiValue`, `publicationToApiValue`, `recordStatusToApiValue`,
 * `difficultyToApiValue`).
 */

export interface CreateCurriculumLessonInput {
  title: string;
  description: string;
  /** BrandonDifficulty */
  difficulty: number;
  thumbnail_path?: string;
  tagIds?: string[];
}

export interface CreateCurriculumChapterInput {
  title: string;
  subtitle: string;
  length: number;
  color: string;
  /** BrandonDifficulty */
  difficulty: number;
  description: string;
  thumbnail_path?: string;
  tagIds?: string[];
  curriculumLessons?: CreateCurriculumLessonInput[];
}

/**
 * Course scalars only. Passing `curriculumChapters` here REPLACES the whole
 * set server-side — use the per-chapter/per-lesson endpoints instead.
 */
export interface UpdateCurriculumCourseInput {
  title?: string;
  description?: string;
  color?: string | null;
  duration?: number | null;
  /** Language */
  language?: number | null;
  /** PublicationStatus */
  publication?: number;
  /** RecordStatus */
  status?: number;
  deadline?: string | null;
  tagIds?: string[] | null;
  lexiconCategoryIds?: string[] | null;
}

export interface UpdateCurriculumChapterInput {
  title?: string;
  subtitle?: string;
  length?: number;
  color?: string;
  /** BrandonDifficulty */
  difficulty?: number;
  description?: string;
  thumbnail_path?: string | null;
  tagIds?: string[] | null;
}

export interface UpdateCurriculumLessonInput {
  title?: string;
  description?: string;
  /** BrandonDifficulty */
  difficulty?: number;
  thumbnail_path?: string | null;
  tagIds?: string[] | null;
}

export interface CreateCurriculumCourseInput {
  title: string;
  description: string;
  color?: string;
  duration?: number;
  /** Language */
  language?: number;
  /** PublicationStatus */
  publication?: number;
  /** RecordStatus */
  status?: number;
  deadline?: string;
  tagIds?: string[];
  curriculumChapters?: CreateCurriculumChapterInput[];
  lexiconCategoryIds?: string[];
}

/** Roster row: an enrollment with the hydrated student summary. */
export interface TeacherCourseEnrollment {
  id: string;
  curriculumCourseId: string;
  enrollmentStatus: CurriculumEnrollmentStatus;
  progressPercentage: number;
  completedChapters: number;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  ownership?: {
    id: string;
    email: string;
    name?: string | null;
    avatarUrl?: string | null;
    createdAt?: string;
  };
}

/** GET curriculum/student/courses/:id/start-readiness */
export interface CourseStartReadiness {
  /** At least one lesson has at least one linked question. */
  ready: boolean;
}

export interface StudentCurrentLesson {
  courseId: string;
  lessonId: string;
  title: string;
  chapterId: string;
  chapterTitle: string;
  type: 'lesson' | 'quiz';
}
